import React, { useEffect, useMemo, createElement as _createElement } from 'react';
import { Image } from 'react-konva';
import { Image as KonvaImage } from 'konva';
import { Box, Point } from '@flatten-js/core';
import { EnhancedPlanarSet } from '@leverege/ui-image-overlay';
import { GlobalState, UI } from '@leverege/ui-redux';
import { Config } from '@leverege/plugin';
import P from '@leverege/promise';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import useImage from 'use-image';
import Util from "./Util.js";
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_LAYER_SETTINGS_PATH = 'rsrc/overlays';
const TYPE = 'api-ui-rsrc-management.layer.ImagineSubRsrcs';
const DEFAULT_OPTS = {
  opacity: 1
};
export default {
  id: TYPE,
  type: TYPE,
  create: async (model, transform, context = {}) => {
    const {
      data
    } = model;
    const {
      id,
      options: pOptions,
      useReact = true
    } = model;
    const {
      clientProps: {
        delegate
      },
      settings
    } = context;
    const options = {
      position: [0, 0],
      ...DEFAULT_OPTS,
      ...pOptions
    };
    const {
      item
    } = options;
    const shape = new EnhancedPlanarSet();
    data.forEach(d => {
      const {
        position = options.position,
        height,
        width
      } = d;
      if (height != null && width != null) {
        shape.add(new Box(position[0], position[1], width, height));
      } else {
        shape.add(new Point(...position));
      }
    });
    const subRsrcs = delegate.getSubRsrcs(item);
    if (useReact) {
      return {
        type: TYPE,
        shape,
        layer: /*#__PURE__*/_jsx(React.Fragment, {
          children: data.map(d => {
            const {
              id,
              key,
              path,
              ...rest
            } = d;
            const opts = {
              ...options,
              ...rest
            };
            const {
              position
            } = opts;
            const rsrc = subRsrcs[key];
            return /*#__PURE__*/_createElement(SubRsrcImage, {
              ...opts,
              id: id,
              key: key,
              position: position,
              rsrc: rsrc,
              delegate: delegate,
              type: key
            });
          })
        }, id)
      };
    }
    const now = DateTime.now();
    const subRsrcProms = data.reduce((prev, curr) => {
      if (subRsrcs) {
        prev[curr.key] = prev[curr.key] || {};
        const subRsrc = subRsrcs[curr.key];
        prev[curr.key] = async () => {
          if (subRsrc.rsrc) {
            const url = new URL(subRsrc.rsrc.data.url);
            let expiresDate;
            if (url.searchParams.has('Expires')) {
              const expires = url.searchParams.get('Expires');
              expiresDate = DateTime.fromMillis(parseInt(expires) * 1000);
            } else if (url.searchParams.has('X-Goog-Date')) {
              const expiresIn = parseInt(url.searchParams.get('X-Goog-Expires'));
              const validAtDate = DateTime.fromISO(url.searchParams.get('X-Goog-Date'));
              expiresDate = validAtDate.plus({
                seconds: expiresIn
              });
            }
            if (expiresDate && expiresDate >= now) {
              return subRsrc.rsrc;
            }
          }
          return GlobalState.dispatch(delegate.item(subRsrc.path).get());
        };
      }
      return prev;
    }, {});
    const subRsrcMap = await P.limit(subRsrcProms, {
      handleReject: true
    });
    const imageMap = await Util.fetchSubRsrcImages(subRsrcMap);

    /**
     * @type {Record<string,HTMLImageElement|null>}
     */
    // const imageMap = await Util.fetchImages( data.map( d => d.url ) )

    return {
      type: TYPE,
      shape,
      layer: data.filter(d => {
        const {
          key
        } = d;
        const images = Object.values(imageMap[key]).filter(i => i != null);
        return images.length > 0;
      }).map(d => {
        const {
          id,
          key,
          ...rest
        } = d;
        const opts = {
          ...options,
          ...rest
        };
        const {
          position
        } = opts;
        const images = imageMap[key];
        return Object.entries(images).reduce((prev, [key, img]) => {
          const id = `${item.id}-${key}`;
          const isVisible = settings?.subRsrc?.[key]?.visible !== false;
          prev.push(new KonvaImage({
            ...opts,
            visible: isVisible,
            id,
            image: img,
            x: position[0],
            y: position[1]
          }));
          return prev;
        }, []);
      })
    };
  }
};
function SubRsrcImage({
  id,
  rsrc,
  position,
  type,
  delegate,
  ...opts
}) {
  const layerSettingsPath = Config.get('OverlayImageRenderers', 'layerSettingsPath', DEFAULT_LAYER_SETTINGS_PATH);
  const settings = useSelector(state => UI.get(state, layerSettingsPath));
  const isVisible = useMemo(() => {
    return settings?.subRsrc?.[type]?.visible !== false;
  }, [settings, type]);
  useEffect(() => {
    if (!rsrc.rsrc) {
      GlobalState.dispatch(delegate.item(rsrc.path).get());
    }
  }, [rsrc]);
  const rsrcData = useSelector(state => delegate.item(rsrc.path).getItem(state));
  const [img, status] = useImage(rsrcData?.data?.url, 'anonymous');
  if (status !== 'loaded') {
    return null;
  }
  return /*#__PURE__*/_jsx(Image, {
    ...opts,
    visible: isVisible,
    id: id,
    image: img,
    x: position[0],
    y: position[1]
  });
}